import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BiCross } from "react-icons/bi";

const DEFAULT_SOUND_SETTINGS = {
    muted: false,
    mutedBackground: false,
    volume: 1,
    volumeBackground: 1,
};

const StateContext = createContext();

export const ContextProvider = ({ children }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [inPointerLock, setInPointerLock] = useState(true);
    const [cursor, setCursor] = useState(<BiCross className="cursor" />);
    const [menu, setMenu] = useState(false);
    const [endScreenOpen, setEndScreenOpen] = useState(false);
    const [roomUrl, setRoomUrl] = useState(null);
    const [itemInputs, setItemInputs] = useState([]);

    const [hideTimer, setHideTimer] = useState(false);
    const [soundSettings, setSoundSettings] = useState(DEFAULT_SOUND_SETTINGS);
    const [graphicsQuality, setGraphicsQuality] = useState("high");
    const [items, setItems] = useState({});
    const [environment, setEnvironment] = useState({});
    const [focusedItem, setFocusedItem] = useState(null);
    const [inspectedItem, setInspectedItem] = useState(null);
    const [displayTextCode, setDisplayTextCode] = useState("");
    const [solved, setSolved] = useState(null);

    useEffect(() => {
        try {
            const storedTimer = localStorage.getItem("hideTimer");
            if (storedTimer !== null) {
                const parsedTimer = JSON.parse(storedTimer);
                queueMicrotask(() => setHideTimer(parsedTimer));
            }

            const storedSound = localStorage.getItem("soundSettings");
            if (storedSound) {
                const parsedSound = JSON.parse(storedSound);
                queueMicrotask(() => setSoundSettings(parsedSound));
            }

            const storedGraphics = localStorage.getItem("graphicsQuality");
            if (storedGraphics) {
                queueMicrotask(() => setGraphicsQuality(storedGraphics));
            }
        } catch (error) {
            console.error("Fehler beim Laden aus dem localStorage:", error);
        }
    }, []);

    const updateSoundSettings = useCallback((updater) => {
        setSoundSettings((prev) => {
            const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
            try {
                localStorage.setItem("soundSettings", JSON.stringify(next));
            } catch (error) {
                console.error("Fehler beim Speichern der Sound-Einstellungen:", error);
            }
            return next;
        });
    }, []);

    const updateGraphicsQuality = useCallback((quality) => {
        setGraphicsQuality(quality);
        try {
            localStorage.setItem("graphicsQuality", quality);
        } catch (error) {
            console.error("Fehler beim Speichern der Grafik-Einstellungen:", error);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const pickedup = useMemo(() => Object.values(items).filter((item) => item.status === "pickedup"), [items]);
    const worldItems = useMemo(() => Object.values(items).filter((item) => item.status === "world"), [items]);
    const physicsItems = useMemo(() => Object.values(items).filter((item) => item.status === "physics"), [items]);
    const placedItems = useMemo(() => Object.values(items).filter((item) => item.status === "placed"), [items]);

    const contextValue = useMemo(
        () => ({
            isMobile,
            setIsMobile,
            hideTimer,
            setHideTimer,
            menu,
            setMenu,
            cursor,
            setCursor,
            inPointerLock,
            setInPointerLock,
            items,
            setItems,
            pickedup,
            itemInputs,
            setItemInputs,
            worldItems,
            physicsItems,
            placedItems,
            focusedItem,
            setFocusedItem,
            solved,
            setSolved,
            inspectedItem,
            setInspectedItem,
            displayTextCode,
            setDisplayTextCode,
            endScreenOpen,
            setEndScreenOpen,
            soundSettings,
            updateSoundSettings,
            graphicsQuality,
            setGraphicsQuality: updateGraphicsQuality,
            updateGraphicsQuality,
            roomUrl,
            setRoomUrl,
            environment,
            setEnvironment,
        }),
        [
            isMobile,
            menu,
            cursor,
            inPointerLock,
            items,
            pickedup,
            worldItems,
            physicsItems,
            placedItems,
            focusedItem,
            solved,
            inspectedItem,
            displayTextCode,
            endScreenOpen,
            soundSettings,
            updateSoundSettings,
            graphicsQuality,
            updateGraphicsQuality,
            roomUrl,
            environment,
            setEnvironment,
            hideTimer,
            setHideTimer,
        ]
    );

    return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
};

export const useStateContext = () => useContext(StateContext);
